export function encodeUrlParams(object: { [key: string]: any }): string {
  const obj = { ...object };

  // Delete some empty filters as the server understands this as "none"
  Object.keys(obj).forEach(key => {
    if (
      ['project_ids', 'user_ids', 'label_ids'].includes(key) &&
      (obj[key] == null || obj[key].length === 0)
    ) {
      delete obj[key];
    }
  });

  // Keys, excluding undefined and null
  const keys = Object.keys(obj).filter(key => obj[key] != null);

  // Sort keys, will affect position in url
  if (Array.isArray(keys)) {
    keys.sort();
  }

  const concatenated = keys
    .reduce<Array<string>>((pairs, key) => {
      const value = obj[key];

      const isArray = Array.isArray(value);
      const isEmptyArray = isArray && value.length === 0;
      const newValue = Array.isArray(value)
        ? value.map(item => encodeURIComponent(item))
        : encodeURIComponent(value);

      // Exclude from url when value is undefined, null or empty array
      if (value != null && !isEmptyArray) {
        pairs.push(`${key}=${newValue}`);
      }

      return pairs;
    }, [])
    .join('&');

  return `?${concatenated}`;
}

export function decodeUrlParams(input: string): any {
  const query: Record<string, any> = {};
  const a = (input[0] === '?' ? input.substr(1) : input).split('&');
  for (let i = 0; i < a.length; i++) {
    const b = a[i].split('=');
    query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || '');
  }

  // convert known array id params to actual array of numbers
  Object.keys(query).forEach(key => {
    // Convert params we know are array of ids:
    if (['labelIds', 'projectIds', 'userIds', 'teamIds', 'clientIds'].includes(key)) {
      query[key] = query[key]
        .split(',')
        // Make sure this can become a number
        .filter((id: string) => id !== '' && !isNaN(Number(id)) && isFinite(Number(id)))
        .map((id: string) => Number(id));
      // If it can be converted into a number, convert it:
    } else if (!isNaN(Number(query[key])) && isFinite(Number(query[key]))) {
      query[key] = Number(query[key]);
    }

    // Convert params we know are array of strings
    if (['columns'].includes(key)) {
      query[key] = query[key].split(',').map((id: string) => id);
    }
  });

  if (!query || input === '') return {};
  return query;
}

export function objectToQueryString(
  params: Record<string, unknown>,
  { leading = false } = {}
): string {
  const queryString = Object.keys(params)
    .filter(p => params[p] != null)
    .map(p => `${p}=${params[p]}`)
    .join('&');

  return leading ? `?${queryString}` : queryString;
}

export function splitOnFirst(input: string, separator: string) {
  if (separator === '') {
    return [input];
  }
  const separatorIndex = input.indexOf(separator);
  return [input.slice(0, separatorIndex), input.slice(separatorIndex + separator.length)];
}

export type RawParseResult<T> = Partial<{ [P in keyof T]: string | Array<string> }>;

/**
 * Alternative implementation of decodeUrlParams
 * - without conversion of known params and conversion to numbers
 * - with known return type
 * - converts comma separated values to Array<string>
 * @param input
 */
export function parseUrlParams<T = any>(input: string): RawParseResult<T> {
  const result = Object.create(null);

  // remove leading ? # or &
  const myInput = input.trim().replace(/^[?#&]/, '');
  const paramGroups = myInput.split('&');

  paramGroups.forEach(group => {
    const leftRightSplit = splitOnFirst(group, '=');
    const key = leftRightSplit[0];
    const value = leftRightSplit[1];

    // Detect comma separated array and convert to Array<string>
    const isArray = value.split('').indexOf(',') > -1;
    result[decodeURIComponent(key)] = isArray
      ? value.split(',').map(item => decodeURIComponent(item))
      : decodeURIComponent(value);
  });

  return result;
}
